import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { user, user_role } from '@prisma/client';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Copy, Loader2, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import useAuth from '~/auth/useAuth';
import { useUsersTableFilteringStore } from '~/stores/users-table-filtering-store';
import Content from "../components/layout/Content";

const generatePassword = (length: number = 16): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*()-_=+[]{}';
    const allChars = uppercase + lowercase + numbers + special;

    let password = '';
    
    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
};

const USER_ROLE_LABELS: Record<user_role, string> = {
    ADMIN: 'Admin',
    OPERATOR: 'Operator',
    USER: 'Benutzer',
    CUSTOMER: 'Kunde',
};

type DialogState = 'form' | 'success';

type FormData = {
    firstName: string;
    lastName: string;
    email: string;
    role: user_role | '';
};

const CreateUserButtonAndModal = () => {
    const navigate = useNavigate();
    const { roles } = useAuth();
    const [open, setOpen] = useState(false);
    const [dialogState, setDialogState] = useState<DialogState>('form');
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
    });
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copiedPassword, setCopiedPassword] = useState(false);
    const [createdUserId, setCreatedUserId] = useState<number | null>(null);

    const utils = trpc.useUtils();
    const createUserMutation = trpc.user.createUser.useMutation({
        onSuccess: (data) => {
            setCreatedUserId(data.id);
            setDialogState('success');
            utils.user.getAll.invalidate();
        },
    });

    const isAdmin = roles.includes('ADMIN');

    const resetForm = useCallback(() => {
        setFormData({ firstName: '', lastName: '', email: '', role: '' });
        setGeneratedPassword('');
        setCopiedPassword(false);
        setCreatedUserId(null);
        setDialogState('form');
        createUserMutation.reset();
    }, [createUserMutation]);

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen) {
            resetForm();
            setGeneratedPassword(generatePassword(16));
        }
        setOpen(newOpen);
    };

    const handleCopyPassword = async () => {
        await navigator.clipboard.writeText(generatedPassword);
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.role) {
            return;
        }
        createUserMutation.mutate({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: generatedPassword,
            role: formData.role,
        });
    };

    const handleGoToUser = () => {
        if (createdUserId) {
            navigate(`/user/${createdUserId}`);
            setOpen(false);
        }
    };

    const isFormValid = useMemo(() => {
        return (
            formData.firstName.trim() !== '' &&
            formData.lastName.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.role !== ''
        );
    }, [formData]);

    if (!isAdmin) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button variant='outline'>
                    <Plus className='mr-2 h-4 w-4' />
                    Neuer Mitarbeiter
                </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-lg'>
                {dialogState === 'form' ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>Neuen Mitarbeiter erstellen</DialogTitle>
                            <DialogDescription>
                                Geben Sie die Daten des neuen Mitarbeiters ein.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className='grid gap-4 py-4'>
                                <div className='grid gap-2'>
                                    <Label htmlFor='firstName'>Vorname</Label>
                                    <Input
                                        id='firstName'
                                        value={formData.firstName}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                                        }
                                        placeholder='Max'
                                        required
                                    />
                                </div>
                                <div className='grid gap-2'>
                                    <Label htmlFor='lastName'>Nachname</Label>
                                    <Input
                                        id='lastName'
                                        value={formData.lastName}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                                        }
                                        placeholder='Mustermann'
                                        required
                                    />
                                </div>
                                <div className='grid gap-2'>
                                    <Label htmlFor='email'>E-Mail</Label>
                                    <Input
                                        id='email'
                                        type='email'
                                        value={formData.email}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, email: e.target.value }))
                                        }
                                        placeholder='max.mustermann@example.com'
                                        required
                                    />
                                </div>
                                <div className='grid gap-2'>
                                    <Label htmlFor='role'>Rolle</Label>
                                    <Select
                                        value={formData.role}
                                        onValueChange={(value: user_role) =>
                                            setFormData((prev) => ({ ...prev, role: value }))
                                        }
                                    >
                                        <SelectTrigger className='w-full'>
                                            <SelectValue placeholder='Rolle auswählen' />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                                                <SelectItem key={value} value={value}>
                                                    {label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className='grid gap-2'>
                                    <Label>Generiertes Passwort</Label>
                                    <div className='flex items-center gap-2'>
                                        <Input
                                            value={generatedPassword}
                                            readOnly
                                            className='font-mono'
                                        />
                                        <Button
                                            type='button'
                                            variant='outline'
                                            size='icon'
                                            onClick={handleCopyPassword}
                                        >
                                            {copiedPassword ? (
                                                <Check className='h-4 w-4 text-green-600' />
                                            ) : (
                                                <Copy className='h-4 w-4' />
                                            )}
                                        </Button>
                                    </div>
                                    <p className='text-xs text-muted-foreground'>
                                        Bitte kopieren Sie das Passwort und geben Sie es dem Mitarbeiter weiter.
                                    </p>
                                </div>
                                {createUserMutation.error && (
                                    <p className='text-sm text-red-600'>
                                        {createUserMutation.error.message}
                                    </p>
                                )}
                            </div>
                            <DialogFooter>
                                <Button
                                    type='button'
                                    variant='outline'
                                    onClick={() => setOpen(false)}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    type='submit'
                                    disabled={!isFormValid || createUserMutation.isPending}
                                >
                                    {createUserMutation.isPending && (
                                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    )}
                                    Erstellen
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Mitarbeiter erfolgreich erstellt</DialogTitle>
                            <DialogDescription>
                                Der neue Mitarbeiter wurde erfolgreich angelegt.
                            </DialogDescription>
                        </DialogHeader>
                        <div className='py-4'>
                            <div className='rounded-lg bg-green-50 p-4 mb-4'>
                                <p className='text-sm text-green-800'>
                                    <strong>{formData.firstName} {formData.lastName}</strong> wurde als{' '}
                                    <strong>{USER_ROLE_LABELS[formData.role as user_role]}</strong> erstellt.
                                </p>
                            </div>
                            <div className='grid gap-2'>
                                <Label>Passwort für den Mitarbeiter</Label>
                                <div className='flex items-center gap-2'>
                                    <Input
                                        value={generatedPassword}
                                        readOnly
                                        className='font-mono'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={handleCopyPassword}
                                    >
                                        {copiedPassword ? (
                                            <Check className='h-4 w-4 text-green-600' />
                                        ) : (
                                            <Copy className='h-4 w-4' />
                                        )}
                                    </Button>
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                    Dies ist die letzte Möglichkeit, das Passwort zu kopieren.
                                </p>
                            </div>
                        </div>
                        <DialogFooter className='flex-col gap-2 sm:flex-row'>
                            <Button
                                type='button'
                                variant='outline'
                                onClick={() => setOpen(false)}
                            >
                                Schließen
                            </Button>
                            <Button
                                type='button'
                                variant='outline'
                                onClick={handleCopyPassword}
                            >
                                {copiedPassword ? (
                                    <Check className='mr-2 h-4 w-4 text-green-600' />
                                ) : (
                                    <Copy className='mr-2 h-4 w-4' />
                                )}
                                Passwort kopieren
                            </Button>
                            <Button type='button' onClick={handleGoToUser}>
                                Zum Mitarbeiter
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

const userColumns: ColumnDef<user>[] = [
    {
        accessorKey: 'id',
        header: 'ID',
    },
    {
        id: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
        cell: ({ row }) => {
            const { firstName, lastName } = row.original;
            const nameParts = [firstName, lastName].filter(Boolean);
            return <div className='font-medium'>{nameParts.join(' ')}</div>;
        },
        accessorFn: (row) => row.lastName,
        enableSorting: true,
        enableHiding: false,
    },
    {
        accessorKey: 'email',
        header: 'E-Mail',
    },
    {
        accessorKey: 'role',
        header: 'Rolle',
        cell: ({ row }) => {
            const role = row.original.role;
            return role;
        },
    },
    {
        id: 'createdAt',
        header: 'Erstellt am',
        cell: ({ row }) => {
            const date = new Date(row.original.createdAt);
            return <div>{format(date, 'dd.MM.yyyy')}</div>;
        },
        accessorFn: (row) => new Date(row.createdAt).getTime(),
        enableSorting: true,
    },
    {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
            if (row.original.deletedAt) {
                return (
                    <span className='inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800'>
                        Gelöscht
                    </span>
                );
            }
            return null;
        },
        enableSorting: false,
    },
];

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    manualPagination?: boolean;
    manualSorting?: boolean;
    manualFiltering?: boolean;
    syncWithUrl?: boolean;
}

const UsersDataTable = <TData, TValue>({
    columns,
    manualPagination = true,
    manualSorting = true,
    manualFiltering = true,
    syncWithUrl = true,
}: DataTableProps<TData, TValue>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);
    const navigate = useNavigate();
    const { roles } = useAuth();
    const isAdmin = roles.includes('ADMIN');
    const [showArchived, setShowArchived] = useState(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useUsersTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    useEffect(() => {
        if (!syncWithUrl || isInitialized.current) return;
        isInitialized.current = true;
        console.log("UsersDataTable: Initializing state from URL/Store...");

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';
        let currentSorting = storeSorting;

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                console.log("Sync: URL -> Store (Sorting)");
                setSorting(urlSortingState);
                currentSorting = urlSortingState;
            }
            if (!searchParams.has('dir') && urlSortField) {
                newParams.set('dir', 'asc');
                needsUrlUpdate = true;
            }
        } else if (storeSorting.length > 0) {
            console.log("Sync: Store -> URL (Sorting)");
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        } else {
            if (searchParams.has('sort')) { newParams.delete('sort'); needsUrlUpdate = true; }
            if (searchParams.has('dir')) { newParams.delete('dir'); needsUrlUpdate = true; }
        }

        const urlFilters: ColumnFiltersState = [];
        let hasUrlFilters = false;
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                hasUrlFilters = true;
                urlFilters.push({ id: key.replace('filter_', ''), value });
            }
        }
        let currentFilters = storeColumnFilters;

        if (hasUrlFilters) {
            const sortedUrlFilters = [...urlFilters].sort((a, b) => a.id.localeCompare(b.id));
            const sortedStoreFilters = [...storeColumnFilters].sort((a, b) => a.id.localeCompare(b.id));
            if (JSON.stringify(sortedUrlFilters) !== JSON.stringify(sortedStoreFilters)) {
                console.log("Sync: URL -> Store (Filters)");
                setColumnFilters(urlFilters);
                currentFilters = urlFilters;
            }
        } else if (storeColumnFilters.length > 0) {
            console.log("Sync: Store -> URL (Filters)");
            storeColumnFilters.forEach(filter => {
                newParams.set(`filter_${filter.id}`, String(filter.value));
            });
            needsUrlUpdate = true;
        }

        const urlPage = parseInt(searchParams.get('page') || '0', 10);
        const urlSize = parseInt(searchParams.get('size') || '10', 10);
        const currentPagination = { pageIndex: urlPage, pageSize: urlSize };

        if (searchParams.has('page') && searchParams.has('size')) {
            if (storePagination.pageIndex !== urlPage || storePagination.pageSize !== urlSize) {
                console.log("Sync: URL -> Store (Pagination)");
                setPagination(currentPagination);
            }
            if (String(currentPagination.pageIndex) !== searchParams.get('page')) {
                newParams.set('page', String(currentPagination.pageIndex));
                needsUrlUpdate = true;
            }
            if (String(currentPagination.pageSize) !== urlSize.toString()) {
                newParams.set('size', String(currentPagination.pageSize));
                needsUrlUpdate = true;
            }
        } else {
            console.log("Sync: Store -> URL (Pagination)");
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        if (needsUrlUpdate) {
            console.log("Applying URL updates:", newParams.toString());
            setSearchParams(newParams, { replace: true });
        }
        console.log("UsersDataTable: Initialization complete.");
    }, [syncWithUrl, searchParams, setSearchParams]);

    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const queryFilters: Record<string, string | undefined> = {};

    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    const {
        data: usersResponse,
        isLoading: isQueryLoading,
        isFetching: isQueryFetching,
    } = trpc.user.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            filters: queryFilters,
            showArchived: isAdmin ? showArchived : false,
        },
        {
            suspense: true,
        }
    );

    const isLoading = isQueryLoading;
    const isFetching = isQueryFetching;

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const currentSortField = searchParams.get('sort');
        const currentSortDir = searchParams.get('dir');
        const storeSortField = storeSorting[0]?.id;
        const storeSortDir = storeSorting[0]?.desc ? 'desc' : 'asc';

        if (storeSortField !== currentSortField || (storeSortField && storeSortDir !== currentSortDir)) {
            console.log("Effect: Store -> URL (Sorting)");
            const newParams = new URLSearchParams(searchParams);
            if (storeSorting.length > 0) {
                newParams.set('sort', storeSorting[0].id);
                newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            } else {
                newParams.delete('sort');
                newParams.delete('dir');
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach(f => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                newParams.set(`filter_${id}`, value);
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            console.log("Effect: Store -> URL (Filters)");
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;
        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');
        const storePageStr = String(storePagination.pageIndex);
        const storeSizeStr = String(storePagination.pageSize);

        if (storePageStr !== urlPage || storeSizeStr !== urlSize) {
            console.log("Effect: Store -> URL (Pagination)");
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePageStr);
            newParams.set('size', storeSizeStr);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, syncWithUrl, searchParams, setSearchParams]);

    const table = useReactTable({
        data: usersResponse?.data || [],
        columns,
        pageCount: usersResponse?.pageCount || 0,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        enableRowSelection: false,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        manualPagination,
        manualSorting,
        manualFiltering,
        getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
        getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    });

    useEffect(() => {
        if (!usersResponse || isLoading || isFetching || !manualPagination) return;
        const currentPageIndex = table.getState().pagination.pageIndex;
        const pageCount = usersResponse.pageCount;
        if (pageCount > 0 && currentPageIndex >= pageCount) {
            console.log(`Clamping page index from ${currentPageIndex} to ${pageCount - 1}`);
            table.setPageIndex(Math.max(0, pageCount - 1));
        }
    }, [usersResponse, table, isLoading, isFetching, manualPagination]);

    if (isLoading && !isInitialized.current) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {Array.from({ length: columns.length }).map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: table.getState().pagination.pageSize }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                    <Skeleton className='h-6 w-24' />
                    <div className="flex space-x-2">
                        <Skeleton className='h-9 w-20' />
                        <Skeleton className='h-9 w-20' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex flex-col gap-2'>
                    <div className='flex gap-4 items-center'>
                        <Input
                            placeholder='Suchen...'
                            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                            onChange={(event) => {
                                table.getColumn('name')?.setFilterValue(event.target.value);
                            }}
                            className='max-w-sm focus:ring-primary'
                        />
                        {isFetching && (
                            <div className='flex items-center text-sm text-muted-foreground'>
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                Loading...
                            </div>
                        )}
                    </div>
                    {isAdmin && (
                        <label className='flex items-center gap-2 text-sm text-muted-foreground cursor-pointer'>
                            <Checkbox
                                checked={showArchived}
                                onCheckedChange={(checked) => setShowArchived(checked === true)}
                            />
                            Gelöschte anzeigen
                        </label>
                    )}
                </div>
                <CreateUserButtonAndModal />
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${row.original.deletedAt ? 'opacity-60' : ''}`}
                                    onClick={() => navigate(`/user/${row.original.id}`)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className='h-24 text-center'>
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Loading data...
                                        </div>
                                    ) : (
                                        storeColumnFilters.length > 0
                                            ? 'Keine Mitarbeiter gefunden.'
                                            : 'No users found.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value))
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Pro Seite {pageSize}
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        &gt;
                    </Button>
                </div>
            </div>
        </div>
    );
};

const Users = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Mitarbeiter</h1>
                <UsersDataTable columns={userColumns} manualPagination={true} manualSorting={true} manualFiltering={true} syncWithUrl={true} />
            </div>
        </Content>
    );
};

export default Users;
